import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../LanguageContext";
import { Headset, MapPinned, Mail, Phone } from "lucide-react";

const Footer = () => {
  const { language } = useLanguage();

  const text = {
    en: {
      contactTitle: "Contact Us",
      subtitle:
        "We look forward to hearing from you. Whether you have questions, need support or are interested in learning more about MEL, we are here to help",
      contactLabel: "Contact MEL at :",
      email: "ferdiyadi@pt-mgi.id",
      phone: "+6231 60018514",

      headOffice: "Head Office",
      headAddress: (
        <>
          Japfa Indoland Center Tower II Level 12 <br />
          Jl. Panglima Sudirman, Kav.66-68 <br />
          Surabaya Pusat - Jawa Timur 60271 <br />
          Telp : (031) – 60018514
        </>
      ),

      workshop: "Workshop Office",
      workshopAddress: (
        <>
          Ruko Central Park Juanda no. CP-01 <br />
          Jl. Pesawon Semampir, Sedati <br />
          Sidoarjo, Jawa Timur <br />
          Telp : (031) – 60018514
        </>
      ),

      mapHeadOffice: "https://maps.app.goo.gl/M17tTWbb2dmhqEKH9",
      mapWorkshop: "https://maps.app.goo.gl/oBRE9J98SF6syPq76",
      seeMap: "See on Maps",
    },

    id: {
      contactTitle: "Kontak Kami",
      subtitle:
        "Kami menantikan kabar dari Anda. Jika Anda memiliki pertanyaan, membutuhkan dukungan, atau ingin mempelajari lebih lanjut tentang MEL, kami siap membantu.",
      contactLabel: "Hubungi MEL di :",
      email: "ferdiyadi@pt-mgi.id",
      phone: "+6231 60018514",

      headOffice: "Kantor Pusat",
      headAddress: (
        <>
          Japfa Indoland Center Tower II Level 12 <br />
          Jl. Panglima Sudirman, Kav.66-68 <br />
          Surabaya Pusat - Jawa Timur 60271 <br />
          Telp : (031) – 60018514
        </>
      ),

      workshop: "Kantor Workshop",
      workshopAddress: (
        <>
          Ruko Central Park Juanda no. CP-01 <br />
          Jl. Pesawon Semampir, Sedati <br />
          Sidoarjo, Jawa Timur <br />
          Telp : (031) – 60018514
        </>
      ),

      mapHeadOffice: "https://maps.app.goo.gl/M17tTWbb2dmhqEKH9",
      mapWorkshop: "https://maps.app.goo.gl/oBRE9J98SF6syPq76",
      seeMap: "Lihat di Maps",
    },
  };

  const t = text[language] || text.en;

  return (
    <motion.footer
      id="footer"
      className="
        relative bg-[#1446A5] text-white rounded-t-3xl 
        px-6 sm:px-10 md:px-16 lg:px-20 py-10 sm:py-16 md:py-20 
        flex flex-col md:flex-row justify-between items-start md:items-center 
        gap-8 overflow-hidden
      "
      initial={{ opacity: 0, scale: 0.9, y: 100 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      viewport={{ once: false, amount: 0.3 }}
    >
      {/* Logo */}
      <motion.div
        className="absolute top-0 right-0 flex flex-row-reverse z-20"
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: false, amount: 0.2 }}
      >
        <div className="bg-white rounded-bl-[40px] w-64 sm:w-72 md:w-80 h-16 sm:h-20 flex items-center justify-center p-2 sm:p-4">
          <img
            src={require('../assets/landing/logomel.jpeg')}
            alt="MEL Logo"
            className="w-32 sm:w-40 md:w-48"
          />
        </div>
      </motion.div>

      {/* Contact */}
      <motion.div
        className={`flex-1 z-10 mt-4 sm:mt-14 ${language === "id" ? "max-w-lg leading-relaxed" : ""}`}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
        viewport={{ once: false, amount: 0.3 }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Headset size={30} />
          <span className="font-semibold text-base sm:text-lg">{t.contactTitle}</span>
        </div>

        <h2 className="text-sm sm:text-base md:text-lg font-semibold leading-snug pb-8">
          {t.subtitle}
        </h2>

        <p className="text-sm sm:text-base font-bold mb-2">{t.contactLabel}</p>
        <a
          href={`mailto:${t.email}`}
          className="flex items-center gap-2 underline text-sm sm:text-base font-semibold hover:text-[#CFE0FF] transition-colors mt-2"
        >
          <Mail size={18} />
          {t.email}
        </a>

        <p className="flex items-center gap-2 text-sm sm:text-base mt-1 font-medium">
          <Phone size={18} />
          {t.phone}
        </p>

      </motion.div>

      <motion.div
        className="flex flex-col items-start md:items-end flex-1 text-left md:text-right z-10 mt-4"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
        viewport={{ once: false, amount: 0.3 }}
      >
        <div className="mb-1 mt-2">
          <h3 className="font-extrabold text-base sm:text-lg mb-2">{t.headOffice}</h3>
          <p className="leading-snug text-sm sm:text-base">{t.headAddress}</p>

          <a
            href={t.mapHeadOffice}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-end gap-2 mt-2 text-white font-semibold text-sm sm:text-base hover:text-[#CFE0FF] transition-colors text-right"

          >
            <MapPinned size={18} />
            {t.seeMap}
          </a>
        </div>

        <div className="mt-5">
          <h3 className="font-extrabold text-base sm:text-lg mb-2 mt-4">{t.workshop}</h3>
          <p className="leading-snug text-sm sm:text-base">{t.workshopAddress}</p>

          <a
            href={t.mapWorkshop}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-end gap-2 mt-2 text-white font-semibold text-sm sm:text-base hover:text-[#CFE0FF] transition-colors text-right"

          >
            <MapPinned size={18} />
            {t.seeMap}
          </a>
        </div>
      </motion.div>
    </motion.footer>
  );
};

export default Footer;
